import React, {
  useState,
  useEffect,
  EventHandler,
  SyntheticEvent,
  useRef,
} from 'react';
import { RootState } from '@Common/models/redux/store';
import { useSelector } from 'react-redux';
import {
  Button,
  Typography,
  Grid,
  Box,
  useTheme,
  IconButton,
  TextField,
  Tooltip,
  Theme,
  MobileStepper,
  ArrowForward as ArrowForwardIcon,
  makeStyles,
} from '@quillbot/design-system';
import LoadingSpinner from 'src/_common/quillElements/spinner/Loading';
import Track from 'src/authentication/handlers/track';
import Head from 'next/head';
import { useRouter } from 'next/router';
import QuillLocalStorage from 'core/utils/localStorageClient';
import {
  addPersonalizeInfo,
  handleReturnUrl,
} from '../handlers/authentication';

interface IProps {
  onNext: Function;
  onSkip: EventHandler<SyntheticEvent>;
  // showContinue?: boolean;
}

const formFields: any = {
  usecases: [
    {
      label: 'School',
      value: 'school',
      questionKey: 'usecase',
      buttonType: 'radio',
      width: 163,
    },
    {
      label: 'Work',
      value: 'work',
      questionKey: 'usecase',
      buttonType: 'radio',
      width: 163,
    },
    {
      label: 'Both',
      value: 'both',
      questionKey: 'usecase',
      buttonType: 'radio',
      width: 163,
    },
    {
      label: 'Other',
      value: 'other',
      questionKey: 'usecase',
      buttonType: 'radio',
      width: 163,
    },
  ],
  educationLevels: [
    {
      label: 'Middle School',
      value: 'middle school',
      questionKey: 'educationLevel',
      buttonType: 'radio',
      width: 210,
      tooltip: 'Primary',
    },
    {
      label: 'High School',
      value: 'high school',
      questionKey: 'educationLevel',
      buttonType: 'radio',
      width: 180,
      tooltip: 'Secondary',
    },
    {
      label: 'Undergraduate',
      value: 'undergraduate',
      questionKey: 'educationLevel',
      buttonType: 'radio',
      width: 222,
      tooltip: 'College',
    },
    {
      label: 'Graduate',
      value: 'graduate',
      questionKey: 'educationLevel',
      buttonType: 'radio',
      width: 173,
      tooltip: 'Masters or PhD',
    },
  ],
  workTypes: [
    {
      label: 'Business',
      value: 'business',
      questionKey: 'workType',
      buttonType: 'multi',
      width: 168,
    },
    {
      label: 'Publishing',
      value: 'publishing',
      questionKey: 'workType',
      buttonType: 'multi',
      width: 180,
    },
    {
      label: 'Journalism',
      value: 'journalism',
      questionKey: 'workType',
      buttonType: 'multi',
      width: 185,
    },

    {
      label: 'Legal',
      value: 'legal',
      questionKey: 'workType',
      buttonType: 'multi',
      width: 136,
    },
    {
      label: 'Travel',
      value: 'travel',
      questionKey: 'workType',
      buttonType: 'multi',
      width: 145,
    },
    {
      label: 'Healthcare',
      value: 'healthcare',
      questionKey: 'workType',
      buttonType: 'multi',
      width: 184,
    },
    {
      label: 'Education',
      value: 'education',
      questionKey: 'workType',
      buttonType: 'multi',
      width: 176,
    },
    {
      label: 'Real Estate',
      value: 'real estate',
      questionKey: 'workType',
      buttonType: 'multi',
      width: 185,
    },
  ],
  languages: {
    english: [
      {
        label: 'No',
        value: 'esl',
        questionKey: 'english',
        buttonType: 'radio',
        width: 120,
      },
      {
        label: 'Yes',
        value: 'native',
        questionKey: 'english',
        buttonType: 'radio',
        width: 120,
      },
    ],
  },
  intents: [
    {
      label: 'Change Sentences',
      value: 'change',
      questionKey: 'intent',
      buttonType: 'multi',
    },
    {
      label: 'Enhance Fluency',
      value: 'fluency',
      questionKey: 'intent',
      buttonType: 'multi',
    },
    {
      label: 'Correct Grammar',
      value: 'grammar',
      questionKey: 'intent',
      buttonType: 'multi',
    },
    {
      label: 'Summarize Text',
      value: 'summarize',
      questionKey: 'intent',
      buttonType: 'multi',
    },
  ],
};

const useStyles = makeStyles((theme) => ({
  personalizeContainer: {
    margin: 'auto',
    marginTop: '40px',
    position: 'relative',
    borderRadius: '5px',
    maxWidth: '960px',
    marginBottom: '10px',
    [theme.breakpoints.down('md')]: {
      marginTop: '28px',
    },
  },
  personalizeContainerInner: {
    paddingTop: '18px',
    height: '50vh',
    overflowY: 'auto',
    [theme.breakpoints.down(411)]: {
      paddingTop: '0px',
    },
  },
  questionContainer: {
    textAlign: 'center',
    marginBottom: '10px',
    justifyContent: 'center',
  },
  btnOption: {
    fontSize: '18px',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    borderRadius: '100px',
    padding: '7px 48px',
    margin: '0px 5px',
    whiteSpace: 'nowrap',
  },
  btnActive: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
  btnContained: {
    '&:hover': {
      color: theme.palette.primary.contrastText,
    },
  },
  question: {
    fontSize: '23px',
    marginTop: '50px',
    marginBottom: '15px',
    padding: '0px 20px',
    [theme.breakpoints.down(411)]: {
      fontSize: '19px',
      marginTop: '37px',
    },
  },
  subheading: {
    fontSize: '15px',
    margin: '15px 0px 12px',
    fontWeight: 400,
  },
  desBox: {
    width: '320px',
    [theme.breakpoints.down(350)]: {
      width: '80%',
    },
  },
  btnBox: {
    marginTop: '10px',
  },
  overFlowContainer: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.down(330)]: {
      flexDirection: 'column',
    },
  },
  quillBtnContainer: {
    // [theme.breakpoints.down('sm')]: {
    //   justifyContent: 'center',
    // },
    position: 'relative',
    justifyContent: 'space-between',
    '& button': {
      borderRadius: '100px',
    },
  },
  singleStepper: {
    fontSize: '10px',
    color: theme.palette.background.default,
  },
  multiStepper: {
    padding: '0px',
    height: '6px',
    backgroundColor: theme.palette.primary.main,
    minWidth: '0px',
    cursor: 'default',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  description: {
    borderRadius: '4px',
    border: `1px solid ${theme.palette.divider}`,
    '&$focus': {
      border: `1px solid ${theme.palette.primary.main} `,
    },
  },
  descriptionLabel: {
    color: theme.palette.error.main,
    '&:focus-visible': {
      color: theme.palette.text.disabled,
    },
  },
  error: {
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main} `,
  },
}));

let timer: any;

function Personalization(props: IProps) {
  let work = true;
  let school = true;
  const router = useRouter();
  const classes = useStyles(props);
  const [activeStep, setActiveStep] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(4);
  const [otherUsecase, setOtherUsecase] = useState('');
  const [otherUsecaseErrorState, setOtherUsecaseErrorState] = useState(false);
  const userState = useSelector((state: RootState) => state.user);
  // const totalQuestions = 3;
  const [answersObj, setAnswers] = useState<{
    english?: unknown;
    usecase?: unknown;
    educationLevel?: unknown;
    workType?: any;
    //  intent?: unknown;
  }>({});
  const [showOtherInput, setOtherInputVisibility] = useState(false);
  const theme = useTheme<Theme>();
  const answersObjRef = useRef<{
    english?: unknown;
    usecase?: unknown;
    educationLevel?: unknown;
    workType?: any;
    //  intent?: unknown;
  }>();
  const [updatedDemographicInfoInDb, setUpdatedDemographicInfoInDbValue] =
    useState(false);
  const updatedDemographicInfoInDbRef = useRef<boolean>();
  const [loading, setLoading] = useState(true);

  // sets the active question on which the user currently is
  const setSteps = (answerData: any) => {
    if (answerData.usecase === 'both' && answerData.educationLevel) {
      setActiveStep(3);
    }
    if (answerData.usecase && answerData.usecase !== 'other') {
      setActiveStep(2);
    }

    if (answerData.english) {
      setActiveStep(1);
    }
  };

  // gets the total number of questions user has to answer based on his usecase
  const getTotalQuestions = (answer: string) => {
    if (answer === 'school' || answer === 'work') {
      setTotalQuestions(3);
      return 3;
    }
    if (answer === 'both') {
      setTotalQuestions(4);
      return 4;
    }
    if (answer === 'other') {
      setTotalQuestions(2);
      return 2;
    }
  };

  // check if the form is completed or not
  const isFormCompleted = (answersData: any) => {
    let questions;
    if (answersData.usecase) {
      questions = getTotalQuestions(answersData.usecase);
      let answeredQues = 0;
      Object.values(answersData).forEach((answer: any) => {
        if (answer?.length) {
          answeredQues += 1;
        }
      });
      if (questions === answeredQues) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    answersObjRef.current = answersObj;
  }, [answersObj]);

  useEffect(() => {
    updatedDemographicInfoInDbRef.current = updatedDemographicInfoInDb;
  }, [updatedDemographicInfoInDb]);

  useEffect(() => {
    window.scrollTo(0, 0);
    let previousAnswersObj = {
      english: userState?.englishSpeaker,
      usecase: userState?.demographic,
      educationLevel: userState?.educationLevel,
      workType: userState?.workType,
    };
    setAnswers(previousAnswersObj);
    const completed = isFormCompleted(previousAnswersObj);
    if (completed) handleReturnUrl(router);
    else {
      Track.shownSection('personalize');
      previousAnswersObj = JSON.parse(
        QuillLocalStorage.getInstance().get('personalizeData', '{}')
      );
      setAnswers(previousAnswersObj);
    }

    setLoading(false);
    return () => {
      if (
        previousAnswersObj !== answersObjRef.current &&
        updatedDemographicInfoInDb
      ) {
        addPersonalizeInfo(answersObjRef.current);
      }
    };
  }, []);

  // get the id of the next question so as to scroll the question into view
  const getNextQuestionId = (answersData: any) => {
    let id = '';
    if (answersData.english && !answersData.usecase) id = 'usecase';
    else if (
      (answersData.usecase === 'school' || answersData.usecase === 'both') &&
      !answersData.educationLevel
    ) {
      id = 'educationLevel';
    } else if (
      answersData.usecase === 'work' ||
      (answersData.usecase === 'both' && !answersData.workType?.length)
    ) {
      id = 'workType';
    }
    return id;
  };

  const scrollQuestion = (id: string) => {
    const ele = document.getElementById(id);
    if (ele)
      ele.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
  };

  const selectOption = (
    answer: string,
    question: 'usecase' | 'educationLevel' | 'workType' | 'english',
    buttonType: string
  ) => {
    const newAnswerObj = { ...answersObj };
    if (question === 'usecase') {
      if (newAnswerObj.educationLevel) newAnswerObj.educationLevel = '';
      if (newAnswerObj.workType?.length) newAnswerObj.workType = [];
      if (answer === 'other' && !showOtherInput) {
        setOtherInputVisibility(true);
      } else {
        setOtherInputVisibility(false);
      }
    }
    if (buttonType === 'radio') {
      if (answer !== answersObj[question]) {
        newAnswerObj[question] = answer;
        setAnswers(newAnswerObj);
      }
    } else {
      if (newAnswerObj[question]) {
        const index = newAnswerObj[question].indexOf(answer);
        if (index > -1) {
          newAnswerObj[question].splice(index, 1);
        } else {
          newAnswerObj[question].push(answer);
        }
      } else newAnswerObj[question] = [answer];
      setAnswers(newAnswerObj);
    }
    setSteps(newAnswerObj);
    setTimeout(() => {
      scrollQuestion(getNextQuestionId(newAnswerObj));
    }, 1000);
    const completed = isFormCompleted(newAnswerObj);
    if (completed) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        addPersonalizeInfo(newAnswerObj);
      }, 2000);
      setUpdatedDemographicInfoInDbValue(true);
    } else {
      QuillLocalStorage.getInstance().set(
        'personalizeData',
        JSON.stringify(newAnswerObj)
      );
    }
    // const localAnswerCache = { ...answersObj };
    // localAnswerCache[question] = 'true';
    // if (Object.keys(localAnswerCache).length === totalQuestions) {
    //   props.onNext();
    // }
  };

  const setOtherValue = () => {
    if (!otherUsecase.length) {
      setOtherUsecaseErrorState(true);
      return;
    }
    setOtherUsecaseErrorState(false);
    const newAnswerObj = { ...answersObj };
    newAnswerObj.usecase = otherUsecase;
    setAnswers(newAnswerObj);
    // if (!updatedDemographicInfoInDb) {
    //   setUpdatedDemographicInfoInDbValue(true);
    //   addPersonalizeInfo(newAnswerObj);
    // }
    props.onNext();
  };

  const heading = (
    <Grid container>
      <Grid item xs={12}>
        <h1 className="pageTitle">Personalize QuillBot</h1>
      </Grid>
      <Grid item xs={12}>
        <Typography style={{ textAlign: 'center' }}>
          How you use QuillBot helps us improve your experience.
        </Typography>
      </Grid>
    </Grid>
  );

  const getButton = (
    label: string,
    value: string,
    questionKey: keyof typeof answersObj,
    buttonType: string,
    width?: number
  ) => (
    <Button
      variant="contained"
      color="primary"
      disableElevation
      style={{ width: `${width}px` }}
      classes={{
        root: classes.btnOption,
        contained: classes.btnContained,
      }}
      className={
        (typeof answersObj[questionKey] === 'string' &&
          answersObj?.[questionKey] === value) ||
        (typeof answersObj[questionKey] === 'object' &&
          answersObj[questionKey]?.includes(value)) ||
        (value === 'other' && showOtherInput)
          ? classes.btnActive
          : ''
      }
      onClick={() => {
        selectOption(value, questionKey, buttonType);
        if (questionKey === 'usecase') getTotalQuestions(value);
      }}
    >
      {label}
    </Button>
  );

  // const showNextButton = () => (
  //   <Grid id="nextBtn" xs={12}>
  //     <Button
  //       variant="contained"
  //       style={{
  //         marginTop: '30px',
  //         padding: '5px 18px',
  //         backgroundColor: theme.specialColors.BLUE_COLOR,
  //         color: theme.palette.primary.contrastText,
  //       }}
  //       onClick={() => {
  //         // if (!updatedDemographicInfoInDb) {
  //         //   addPersonalizeInfo(answersObj);
  //         //   setUpdatedDemographicInfoInDbValue(true);
  //         // }
  //         props.onNext();
  //       }}
  //     >
  //       Next
  //     </Button>
  //   </Grid>
  // );

  const showEducationLevelQuestion = (schoolVar = true) => {
    school = schoolVar;
    return (
      <Grid
        justifyContent="center"
        id={formFields.educationLevels[0].questionKey}
        container
      >
        <Grid item xs={12}>
          <Typography classes={{ body1: classes.question }}>
            What is your education level?
          </Typography>
        </Grid>
        <Box display="flex" className={classes.overFlowContainer}>
          {formFields.educationLevels.map((educationLevel: any) => (
            <Box mt={2} className={classes.btnBox} key={educationLevel.label}>
              <Tooltip
                enterDelay={1000}
                placement="top"
                title={educationLevel.tooltip}
              >
                {getButton(
                  educationLevel.label,
                  educationLevel.value,
                  educationLevel.questionKey,
                  educationLevel.buttonType,
                  educationLevel.width
                )}
              </Tooltip>
            </Box>
          ))}
        </Box>
      </Grid>
    );
  };

  const showWorkTypeQuestion = (workVar = true) => {
    work = workVar;

    return (
      <Grid
        justifyContent="center"
        id={formFields.workTypes[0].questionKey}
        container
      >
        <Grid item xs={12}>
          <Typography
            style={{ marginBottom: '0px' }}
            classes={{ body1: classes.question }}
          >
            What type of professional work do you use QuillBot for?
          </Typography>
          <Typography classes={{ body1: classes.subheading }}>
            *Choose as many as you like
          </Typography>
        </Grid>

        <Box
          display="flex"
          style={{ maxWidth: '850px' }}
          className={classes.overFlowContainer}
        >
          {formFields.workTypes.map((educationLevel: any) => (
            <Box mt={2} className={classes.btnBox} key={educationLevel.label}>
              {getButton(
                educationLevel.label,
                educationLevel.value,
                educationLevel.questionKey,
                educationLevel.buttonType,
                educationLevel.width
              )}
            </Box>
          ))}
        </Box>
      </Grid>
    );
  };

  const showBothQuestions = () => (
    <>
      {showEducationLevelQuestion(false)}
      {answersObj.educationLevel && showWorkTypeQuestion(false)}
    </>
  );

  // const activeStepperComponent = () =>
  //    (
  //     <>
  //       <FiberManualRecordIcon
  //         className={classes.singleStepper}
  //         style={{ color: theme.palette.primary.main }}
  //       />
  //       {totalQuestions === 2 && activeStep === 2 ? (
  //         <Button className={classes.multiStepper} style={{ width: '10px' }} />
  //       ) : totalQuestions === 3 && activeStep === 3 ? (
  //         <Button className={classes.multiStepper} style={{ width: '5px' }} />
  //       ) : (
  //         <>
  //           <FiberManualRecordIcon className={classes.singleStepper} />
  //           <FiberManualRecordIcon className={classes.singleStepper} />
  //           <FiberManualRecordIcon className={classes.singleStepper} />
  //         </>
  //       )}
  //     </>
  //   );

  return (
    <>
      {loading ? (
        <div>
          <LoadingSpinner isLoading={loading} />
        </div>
      ) : (
        <>
          <Head>
            <title>Personalize | QuillBot</title>
          </Head>
          <div className="container">
            <Grid container alignItems="center">
              {heading}
              <Grid item xs={12}>
                <Box boxShadow={3} className={classes.personalizeContainer}>
                  {/* <Grid container className="auth-container-top-border" /> */}
                  <Grid
                    container
                    direction="column"
                    alignItems="center"
                    className={classes.personalizeContainerInner}
                    spacing={1}
                  >
                    <Grid
                      id="questionContainer"
                      item
                      container
                      className={classes.questionContainer}
                    >
                      <Grid item xs={12}>
                        <Typography classes={{ body1: classes.question }}>
                          Is English your first language?
                        </Typography>
                      </Grid>
                      <Grid container item xs={12} justifyContent="center">
                        {formFields.languages.english.map((option: any) => (
                          <Box
                            mt={2}
                            className={classes.btnBox}
                            key={option.label}
                          >
                            {getButton(
                              option.label,
                              option.value,
                              option.questionKey,
                              option.buttonType,
                              option.width
                            )}
                          </Box>
                        ))}
                      </Grid>
                      {answersObj.english && (
                        <Grid
                          justifyContent="center"
                          id={formFields.usecases[0].questionKey}
                          item
                          container
                        >
                          <Grid item xs={12}>
                            <Typography classes={{ body1: classes.question }}>
                              Most of my writing is used for:
                            </Typography>
                          </Grid>
                          <Box
                            display="flex"
                            className={classes.overFlowContainer}
                          >
                            {formFields.usecases.map((usecase: any) => (
                              <Box
                                mt={2}
                                className={classes.btnBox}
                                key={usecase.label}
                              >
                                {getButton(
                                  usecase.label,
                                  usecase.value,
                                  usecase.questionKey,
                                  usecase.buttonType,
                                  usecase.width
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Grid>
                      )}
                      {showOtherInput && (
                        <Grid item container xs={12} justifyContent="center">
                          <Box mt={2} className={classes.desBox}>
                            <TextField
                              fullWidth
                              size="small"
                              variant="filled"
                              error={otherUsecaseErrorState}
                              helperText={otherUsecaseErrorState && 'Required'}
                              label="Describe"
                              onChange={(e) => {
                                setOtherUsecase(e.target.value.trim());
                                setOtherUsecaseErrorState(false);
                              }}
                              InputProps={{
                                disableUnderline: true,
                                classes: {
                                  root: classes.description,
                                  error: classes.error,
                                },
                                endAdornment: (
                                  <IconButton
                                    style={{ padding: '0px' }}
                                    onClick={setOtherValue}
                                    size="large"
                                  >
                                    <ArrowForwardIcon
                                      style={{
                                        color:
                                          theme.palette.primary.contrastText,
                                        backgroundColor:
                                          theme.specialColors.BLUE_COLOR,
                                        borderRadius: '50%',
                                        padding: '3px',
                                      }}
                                    />
                                  </IconButton>
                                ),
                              }}
                              InputLabelProps={{
                                className: classes.descriptionLabel,
                                style: {
                                  color: otherUsecaseErrorState
                                    ? theme.palette.error.main
                                    : theme.palette.text.disabled,
                                },
                              }}
                              value={otherUsecase}
                            />
                            {/* <MyInputBox
                        fullWidth
                        label="Description"
                        size="small"
                        variant="filled"
                        onChange={(e) => setOtherUsecase(e.target.value)}
                        InputProps={{
                          classes: { error: classes.error },
                          disableUnderline: true,
                        }}
                        InputLabelProps={{
                          className: classes.descriptionLabel,
                        }}
                      /> */}
                          </Box>
                        </Grid>
                      )}
                      {answersObj.usecase === 'school' &&
                        showEducationLevelQuestion()}
                      {answersObj.usecase === 'work' && showWorkTypeQuestion()}
                      {answersObj.usecase === 'both' && showBothQuestions()}

                      {/* {answersObj.english && (
                  <>
                    <Grid item xs={12}>
                      <Typography
                        style={{ marginBottom: '0px' }}
                        classes={{ body1: classes.question }}>
                        I use Quillbot mainly to:
                      </Typography>
                      <Typography classes={{ body1: classes.subheading }}>
                        *Choose as many as you like
                      </Typography>
                    </Grid>
                    <Grid container item xs={12} justify="center">
                      {formFields.intents.map((intent: any) => (
                        <Box mt={2} className={classes.btnBox} key={intent.label}>
                          {getButton(
                            intent.label,
                            intent.value,
                            intent.questionKey,
                            intent.buttonType,
                            intent.width
                          )}
                        </Box>
                      ))}
                    </Grid>
                  </>
                )} */}
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    container
                    justifyContent="center"
                    className={classes.quillBtnContainer}
                  >
                    {/* <Link to="/"> */}
                    <Grid item>
                      <Button
                        style={{
                          color: theme.palette.primary.main,
                          marginLeft: '8px',
                        }}
                        onClick={(e) => {
                          props.onSkip(e);
                          // if (!updatedDemographicInfoInDb) {
                          //   addPersonalizeInfo(answersObj);
                          //   setUpdatedDemographicInfoInDbValue(true);
                          // }
                        }}
                      >
                        Skip
                      </Button>
                    </Grid>
                    <Grid
                      item
                      justifyContent="center"
                      alignItems="center"
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%,-50%)',
                        display: 'flex',
                        flexGrow: 1,
                      }}
                    >
                      <MobileStepper
                        variant="dots"
                        steps={totalQuestions}
                        position="static"
                        activeStep={activeStep}
                        backButton={false}
                        nextButton={false}
                        style={{ backgroundColor: theme.palette.grey[50] }}
                      />
                      {/* {activeStepperComponent()} */}
                    </Grid>
                    {((school && answersObj.educationLevel) ||
                      (work &&
                        answersObj.workType &&
                        answersObj.workType.length !== 0) ||
                      (answersObj.educationLevel &&
                        answersObj.workType &&
                        answersObj.workType.length !== 0)) && (
                      // eslint-disable-next-line react/jsx-indent
                      <Grid item>
                        <Button
                          style={{
                            padding: '5px 18px',
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            marginRight: '8px',
                          }}
                          onClick={(e) => {
                            props.onNext();
                          }}
                        >
                          Next
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </div>
        </>
      )}
    </>
  );
}
export default Personalization;
